/**
 * The `timeline` chart kind of the one chart engine (chartEngine.js re-exports it): a Gantt of
 * tasks, one row per task, grouped by board column. It returns plain primitives (rows, bars,
 * diamonds, ticks, weekend bands, the today line, group headers) that TimelineSvg.vue draws.
 * Every task gets a row. The geometry comes in two bands that share one coordinate system: the
 * header (tick labels, the Today label, the axis line) and the body (grid, rows), so the header
 * can stay at the top while a chat card scrolls the body (`scroll`).
 *
 * Spec: { kind: 'timeline', layout?: 'gantt'|'lanes'|'strip', title, subtitle?, today: 'YYYY-MM-DD',
 *   range?: { from, to }, groups: [{ label, items: [{ id, label, start|null, end, status,
 *   state: 'todo'|'doing'|'review'|'done', overdue, assignee?, taskId?, taskTypeId?, facilityId?,
 *   description?, tag? }] }] }
 * `id` is the display ref (T-3107); taskId / taskTypeId / facilityId are the real ids "Open in task
 * board" needs, and description / tag optional popover lines (timelineTask.js cleans them).
 * The server sends state, overdue and today (the engine never guesses them); the engine decides
 * colours, ticks, sizes and cuts. Only the Gantt is built: every layout draws as one for now.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { dataColor, widthOf, fixed } from './chartBasics.js';
import { dayNumber, yearOf, isWeekend, formatDay, fitText, textWidth, makeScale, axisTicks, TICK_UNIT } from './timelineScale.js';
import { taskFields } from './timelineTask.js';

export const TIMELINE_LAYOUT = Object.freeze({ GANTT: 'gantt', LANES: 'lanes', STRIP: 'strip' });

/** Board state → legend label and palette colour, in board order. */
export const TIMELINE_STATES = Object.freeze({
  todo: Object.freeze({ label: 'To do', color: 'blue' }),
  doing: Object.freeze({ label: 'In progress', color: 'purple' }),
  review: Object.freeze({ label: 'Awaiting approval', color: 'orange' }),
  done: Object.freeze({ label: 'Done', color: 'teal' }),
});
const DEFAULT_STATE = 'todo';
const STATE_ORDER = Object.keys(TIMELINE_STATES);
export const timelineStateColor = (state) => dataColor((TIMELINE_STATES[state] || TIMELINE_STATES[DEFAULT_STATE]).color);

/** Below this width the card is "narrow" (side panel): smaller rows and no due labels. */
export const NARROW_WIDTH = 520;
/** A chat card shows about this many rows, then the body scrolls under the sticky header. */
export const TIMELINE_SCROLL_ROWS = 14;
/** How far the row hit area and focus ring reach past the chart on each side (a scroll box pads by it). */
export const HIT_BLEED = 4;
export const TODAY_LABEL = 'Today';
/** Small filled warning sign, 12 x 11, drawn beside overdue bars so overdue never relies on colour alone. */
export const OVERDUE_ICON = Object.freeze({
  triangle: 'M6 .6 11.6 10.6H.4Z',
  bar: Object.freeze({ x: 5.35, y: 3.6, width: 1.3, height: 3.9, rx: 0.65 }),
  dot: Object.freeze({ cx: 6, cy: 8.85, r: 0.8 }),
});
/** The overdue hatching: light stripes over the state colour (an SVG pattern, chart and legend). */
export const OVERDUE_HATCH = Object.freeze({
  pattern: Object.freeze({ width: 5, height: 5, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(45)' }),
  stripe: Object.freeze({ width: 2, height: 5, 'fill-opacity': 0.75 }),
});
/** Height of the tick label band at the top of the header. */
const AXIS_H = 22;
/** Grid lines start this far above the axis line; tick labels sit on this baseline above it. */
const TICK_RISE = 6;
const TICK_BASELINE = 9;
const SIZE = Object.freeze({
  narrow: { row: 24, bar: 12 },
  wide: { row: 26, bar: 14 },
  groupHead: 22,
  bodyTop: 2,
  bottom: 4,
  nameGap: 12,
  right: 6,
});
const FONT = Object.freeze({ name: 11, due: 10, semibold: 600 });
const LABEL_COLUMN = Object.freeze({ narrow: { share: 0.36, min: 96, max: 150 }, wide: { share: 0.3, min: 170, max: 230 } });
const MIN_BAR_PX = 4;
const BAR_RADIUS = 3;
const OVERLAY_INSET = 0.75;
const CHEVRON = Object.freeze({ gap: 2, depth: 4, half: 4 });
const WARN = Object.freeze({ gap: 4, width: 16, half: 5.5 });
/** The Today pill gets its own band in the header, under the tick labels: offset above and below it. */
const TODAY_PILL = Object.freeze({ h: 15, padX: 10, offset: 3, baseline: 11, edge: 4 });
const TODAY_BAND = TODAY_PILL.h + 2 * TODAY_PILL.offset;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const text = (v) => (v === null || v === undefined ? '' : String(v));

/** A clean copy of one item, or null when its dates are unusable (bad date, end before start). */
function normaliseItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const end = dayNumber(raw.end);
  const hasStart = !(raw.start === null || raw.start === undefined || raw.start === '');
  const start = hasStart ? dayNumber(raw.start) : null;
  if (end === null || (hasStart && (start === null || start > end))) return null;
  const state = Object.prototype.hasOwnProperty.call(TIMELINE_STATES, raw.state) ? raw.state : DEFAULT_STATE;
  const item = {
    id: text(raw.id), label: text(raw.label || raw.id), start: hasStart ? raw.start : null, end: raw.end,
    status: text(raw.status), state, overdue: raw.overdue === true, assignee: text(raw.assignee), ...taskFields(raw),
  };
  return { item, start, end };
}

/** Validated spec, or null when nothing can be drawn. */
function normalise(spec) {
  const today = dayNumber(spec && spec.today);
  if (today === null || !Array.isArray(spec.groups)) return null;
  let skipped = 0;
  const groups = spec.groups.flatMap((g) => {
    const rawItems = g && Array.isArray(g.items) ? g.items : [];
    const items = rawItems.map(normaliseItem).filter(Boolean);
    skipped += rawItems.length - items.length;
    return items.length ? [{ label: text(g.label), items }] : [];
  });
  const items = groups.flatMap((g) => g.items);
  if (!items.length) return null;
  const from = dayNumber(spec.range && spec.range.from);
  const to = dayNumber(spec.range && spec.range.to);
  const range = from !== null && to !== null && from <= to ? { from, to } : null;
  return { today, range, groups, items, skipped };
}

function emptyGeometry(width, skipped = 0) {
  return {
    kind: 'timeline', layout: TIMELINE_LAYOUT.GANTT, empty: true, skipped, asOf: null, viewBox: `0 0 ${width} 0`, width, height: 0,
    header: { height: 0, viewBox: `0 0 ${width} 0` }, body: { y: 0, height: 0, viewBox: `0 0 ${width} 0` },
    scroll: { maxHeight: 0, needed: false },
    plot: null, axis: null, ticks: { unit: null, step: 0, grid: [], stubs: [], labels: [] }, weekends: [], today: null,
    groups: [], rows: [], legend: { states: [], overdue: false, dueOnly: false, today: false },
  };
}

const diamondPoints = (cx, cy, r) => [[cx, cy - r], [cx + r, cy], [cx, cy + r], [cx - r, cy]].map((p) => p.map(fixed).join(',')).join(' ');
const chevron = (x, cy, dir) => `M${fixed(x)} ${fixed(cy - CHEVRON.half)} L${fixed(x + dir * CHEVRON.depth)} ${fixed(cy)} L${fixed(x)} ${fixed(cy + CHEVRON.half)}`;

/** A bar from start to end (inclusive), or a diamond on the due date when there is no start. */
function barShape(m, sc, cy, h) {
  if (m.start === null) {
    const cx = clamp(sc.x(m.end + 0.5), sc.left + h / 2, sc.right - h / 2);
    const r = h / 2 + 1;
    const points = diamondPoints(cx, cy, r);
    return { bar: { kind: 'diamond', points }, overlay: { kind: 'diamond', points }, cuts: [], x1: cx + r };
  }
  let x0 = sc.x(m.start);
  let x1 = sc.x(m.end + 1);
  const cutL = x0 < sc.left - 0.5;
  const cutR = x1 > sc.right + 0.5;
  x0 = clamp(x0, sc.left, sc.right);
  x1 = clamp(x1, sc.left, sc.right);
  if (x1 - x0 < MIN_BAR_PX) {
    if (x0 + MIN_BAR_PX <= sc.right) x1 = x0 + MIN_BAR_PX;
    else x0 = x1 - MIN_BAR_PX;
  }
  const y = cy - h / 2;
  const bar = { kind: 'rect', x: fixed(x0), y: fixed(y), w: fixed(x1 - x0), h, rx: BAR_RADIUS };
  const overlay = { kind: 'rect', x: fixed(x0 + OVERLAY_INSET), y: fixed(y + OVERLAY_INSET), w: fixed(x1 - x0 - 2 * OVERLAY_INSET), h: h - 2 * OVERLAY_INSET, rx: BAR_RADIUS };
  const cuts = [];
  if (cutL) cuts.push(chevron(x0 - CHEVRON.gap, cy, -1));
  if (cutR) cuts.push(chevron(x1 + CHEVRON.gap, cy, 1));
  return { bar, overlay, cuts, x1: x1 + (cutR ? CHEVRON.gap + CHEVRON.depth : 0) };
}

/** "Name · 6 Oct to 17 Oct · In progress · R. Menon · Overdue" (the year only when it is not this year). */
function ariaFor(m, todayYear) {
  const day = (n) => formatDay(n, yearOf(n) !== todayYear);
  const when = m.start === null ? `due ${day(m.end)}` : `${day(m.start)} to ${day(m.end)}`;
  return [m.item.label, when, m.item.status, m.item.assignee, m.item.overdue ? 'Overdue' : ''].filter(Boolean).join(' · ');
}

function ganttRow(m, ctx, y, index) {
  const { sc, size, narrow, labelW, width } = ctx;
  const cy = y + size.row / 2;
  const shape = barShape(m, sc, cy, size.bar);
  const overdue = m.item.overdue;
  let end = shape.x1;
  const warn = overdue ? { x: fixed(end + WARN.gap), y: fixed(cy - WARN.half) } : null;
  if (overdue) end += WARN.width;
  const dueText = formatDay(m.end);
  const showDue = !narrow && end + 6 + textWidth(dueText, FONT.due) <= width;
  return {
    key: `i${index}`, item: m.item, aria: ariaFor(m, ctx.todayYear), color: timelineStateColor(m.item.state), overdue,
    name: { x: 0, y: fixed(cy + 4), ...fitText(m.item.label, labelW - 4, FONT.name) },
    bar: shape.bar, overlay: overdue ? shape.overlay : null, cuts: shape.cuts, warn,
    due: showDue ? { x: fixed(end + 5), y: fixed(cy + 3.5), text: dueText } : null,
    hit: { x: -HIT_BLEED, y: y + 1, w: width + 2 * HIT_BLEED, h: size.row - 2 },
  };
}

const todayInRange = (sc, today) => today >= sc.lo && today < sc.hi;

/**
 * The today line: its label (a pill) in the header under the tick labels, then the dashed line in
 * two parts, `head` (pill to axis, header) and y1..y2 (axis to the last row, body).
 */
function todayMarker(sc, today, headerH, bottom) {
  const x = fixed(sc.x(today + 0.5));
  const w = textWidth(TODAY_LABEL, FONT.due, FONT.semibold) + TODAY_PILL.padX;
  const px = fixed(clamp(x - w / 2, sc.left - TODAY_PILL.edge, sc.right - w + TODAY_PILL.edge));
  const pillY = AXIS_H + TODAY_PILL.offset;
  return {
    x, y1: headerH, y2: bottom, head: { y1: pillY + TODAY_PILL.h, y2: headerH },
    pill: { x: px, y: pillY, w: fixed(w), h: TODAY_PILL.h, rx: TODAY_PILL.h / 2 },
    label: { x: fixed(px + w / 2), y: pillY + TODAY_PILL.baseline, text: TODAY_LABEL },
  };
}

function legendOf(items, today) {
  return {
    states: STATE_ORDER.filter((s) => items.some((m) => m.item.state === s)).map((key) => ({ key, label: TIMELINE_STATES[key].label, color: timelineStateColor(key) })),
    overdue: items.some((m) => m.item.overdue),
    dueOnly: items.some((m) => m.start === null),
    today: !!today,
  };
}

/** The tallest a scrolling chat card gets: the header, one group head and TIMELINE_SCROLL_ROWS rows. */
const scrollMaxHeight = (headerH, size) => headerH + SIZE.bodyTop + SIZE.groupHead + TIMELINE_SCROLL_ROWS * size.row;

/**
 * @param {object} spec timeline spec
 * @param {{ width?: number }} [opts] width = the chart's real px width, so text keeps its size
 * The result's `asOf` is today as a UTC day number (the task popover words its dates from it).
 */
export function timelineGeometry(spec, { width: w } = {}) {
  const width = widthOf(w);
  const model = normalise(spec);
  if (!model) return emptyGeometry(width, 0);
  const narrow = width < NARROW_WIDTH;
  const size = narrow ? SIZE.narrow : SIZE.wide;
  const col = narrow ? LABEL_COLUMN.narrow : LABEL_COLUMN.wide;
  const labelW = clamp(Math.round(width * col.share), col.min, col.max);
  const sc = makeScale(model, labelW + SIZE.nameGap, width - SIZE.right);
  const ctx = { sc, size, narrow, labelW, width, todayYear: yearOf(model.today) };
  const showToday = todayInRange(sc, model.today);
  const headerH = AXIS_H + (showToday ? TODAY_BAND : 0);

  const groups = [];
  const rows = [];
  let y = headerH + SIZE.bodyTop;
  model.groups.forEach((g, gi) => {
    const label = { x: 0, y: y + 15, ...fitText(g.label, labelW - 22, FONT.name, FONT.semibold) };
    const count = { x: fixed(textWidth(label.text, FONT.name, FONT.semibold) + 6), y: y + 15, text: String(g.items.length) };
    groups.push({ divider: gi ? y : null, label, count });
    y += SIZE.groupHead;
    g.items.forEach((m) => {
      rows.push(ganttRow(m, ctx, y, rows.length));
      y += size.row;
    });
  });

  const axis = axisTicks(sc);
  const ticks = {
    unit: axis.unit,
    step: axis.step,
    grid: axis.grid.map((x) => ({ x, y1: headerH, y2: y })),
    stubs: axis.grid.map((x) => ({ x, y1: AXIS_H - TICK_RISE, y2: headerH })),
    labels: axis.labels.map((l) => ({ ...l, y: AXIS_H - TICK_BASELINE })),
  };
  const weekends = [];
  if (ticks.unit === TICK_UNIT.DAY) {
    for (let n = sc.lo; n < sc.hi; n += 1) if (isWeekend(n)) weekends.push({ x: fixed(sc.x(n)), y: headerH, w: fixed(sc.k), h: y - headerH });
  }
  const today = showToday ? todayMarker(sc, model.today, headerH, y) : null;
  const height = Math.ceil(y + SIZE.bottom);
  const bodyH = height - headerH;
  const maxHeight = scrollMaxHeight(headerH, size);
  return {
    kind: 'timeline', layout: TIMELINE_LAYOUT.GANTT, empty: false, skipped: model.skipped, asOf: model.today,
    viewBox: `0 0 ${width} ${height}`, width, height,
    header: { height: headerH, viewBox: `0 0 ${width} ${headerH}` },
    body: { y: headerH, height: bodyH, viewBox: `0 ${headerH} ${width} ${bodyH}` },
    scroll: { maxHeight, needed: height > maxHeight },
    plot: { left: sc.left, right: sc.right, bottom: y },
    axis: { x1: 0, x2: width, y: headerH }, ticks, weekends, today, groups, rows, legend: legendOf(model.items, today),
  };
}
